import AddBoxSharpIcon from '@mui/icons-material/AddBoxSharp';
import DisabledByDefaultSharpIcon from '@mui/icons-material/DisabledByDefaultSharp';
import { ButtonBase, Stack, Typography } from '@mui/material';
import {
  QuestionnaireLanguages,
  i18nField,
  i18nQuestion,
} from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { BasicActionDialog } from '../../../ui/base-dialogs';

interface Props {
  open: boolean;
  categoryId?: string;
  name: i18nField;
  description: i18nField;
  questions: i18nQuestion[];

  handleDimensionNameChange(lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleDimensionDescriptionChange(lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  addNewQuestionToDimensionSubCategory(): void;

  handleDimensionSelfAssessmentQuestionChange(
    questionIndex: number,
    lang: QuestionnaireLanguages
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleDimensionExternalAssessmentQuestionChange(
    questionIndex: number,
    lang: QuestionnaireLanguages
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;

  removeQuestionFromDimension(questionIndex: number): () => void;

  handleClose(): void;

  addDimension(): void;
}

const AddDimensionDialog: React.FC<Props> = ({
  open,
  categoryId,
  name,
  description,
  questions,
  handleDimensionDescriptionChange,
  handleDimensionNameChange,
  addNewQuestionToDimensionSubCategory,
  handleDimensionSelfAssessmentQuestionChange,
  handleDimensionExternalAssessmentQuestionChange,
  removeQuestionFromDimension,
  handleClose,
  addDimension,
}) => {
  const { classes } = useStyles();

  const isEmpty = () => {
    if (name.de.trim() === '' || name.en.trim() === '') {
      return true;
    }

    if (categoryId && (description.de.trim() === '' || description.en.trim() === '')) {
      return true;
    }

    if (
      questions.some((question) => question.selfAssessment.de.trim() === '') ||
      questions.some((question) => question.selfAssessment.en.trim() === '') ||
      questions.some((question) => question.externalAssessment.de.trim() === '') ||
      questions.some((question) => question.externalAssessment.en.trim() === '')
    ) {
      return true;
    }

    return false;
  };

  return (
    <BasicActionDialog
      dialogTitle={'Dimension hinzufügen'}
      maxWidth={'md'}
      cancelButtonText={'Abbrechen'}
      proceedButtonText={'Hinzufügen'}
      open={open}
      handleClose={handleClose}
      handleProceed={addDimension}
      proceedButtonDisabled={isEmpty()}
    >
      <Stack gap={2}>
        <Stack className={classes.container} gap={2}>
          <Stack direction={'row'} alignContent={'center'} gap={2}>
            <PaiTextField
              value={name.de}
              onChange={handleDimensionNameChange(QuestionnaireLanguages.DE)}
              label={'Unterkategorie (deutsch)'}
              placeholder={'Name der Dimension eingeben...'}
            />
            <PaiTextField
              value={name.en}
              onChange={handleDimensionNameChange(QuestionnaireLanguages.EN)}
              label={'Unterkategorie (englisch)'}
              placeholder={'Name der Dimension eingeben...'}
            />
          </Stack>
          {!_.isNil(categoryId) ? (
            <Stack direction={'row'} alignContent={'center'} gap={2}>
              <PaiTextField
                value={description.de}
                onChange={handleDimensionDescriptionChange(QuestionnaireLanguages.DE)}
                label={'Beschreibung (deutsch)'}
                placeholder={'Beschreibung der Dimension eingeben...'}
              />
              <PaiTextField
                value={description.en}
                onChange={handleDimensionDescriptionChange(QuestionnaireLanguages.EN)}
                label={'Beschreibung (englisch)'}
                placeholder={'Beschreibung der Dimension eingeben...'}
              />
            </Stack>
          ) : null}
        </Stack>
        {questions.map((question, index) => (
          <Stack key={index} className={classes.container} gap={2}>
            <Stack direction={'row'} alignContent={'center'} gap={2}>
              <PaiTextField
                value={question.selfAssessment.de}
                placeholder={'Fragetext zur Selbstbewertung...'}
                label={'Selbstbewertung (deutsch)'}
                onChange={handleDimensionSelfAssessmentQuestionChange(index, QuestionnaireLanguages.DE)}
              />
              <PaiTextField
                value={question.selfAssessment.en}
                placeholder={'Fragetext zur Selbstbewertung...'}
                label={'Selbstbewertung (englisch)'}
                onChange={handleDimensionSelfAssessmentQuestionChange(index, QuestionnaireLanguages.EN)}
              />
            </Stack>
            <Stack key={index} direction={'row'} alignContent={'center'} gap={2}>
              <PaiTextField
                value={question.externalAssessment.de}
                placeholder={'Fragetext zur Fremdbewertung...'}
                label={'Fremdbewertung (deutsch)'}
                onChange={handleDimensionExternalAssessmentQuestionChange(index, QuestionnaireLanguages.DE)}
              />
              <PaiTextField
                value={question.externalAssessment.en}
                placeholder={'Fragetext zur Fremdbewertung...'}
                label={'Fremdbewertung (englisch)'}
                onChange={handleDimensionExternalAssessmentQuestionChange(index, QuestionnaireLanguages.EN)}
              />
            </Stack>
            {questions.length > 1 ? (
              <Stack direction={'row'} justifyContent={'flex-end'}>
                <ButtonBase className={classes.deleteButton} onClick={removeQuestionFromDimension(index)}>
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                    <DisabledByDefaultSharpIcon className={classes.icon} />
                    <Typography className={classes.text}>Frage entfernen</Typography>
                  </Stack>
                </ButtonBase>
              </Stack>
            ) : null}
          </Stack>
        ))}
        <ButtonBase className={classes.addButton} onClick={addNewQuestionToDimensionSubCategory}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
            <AddBoxSharpIcon className={classes.icon} />
            <Typography className={classes.text}>weitere Frage</Typography>
          </Stack>
        </ButtonBase>
      </Stack>
    </BasicActionDialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    paddingBottom: '16px',
  },
  deleteButton: {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  addButton: {
    marginRight: 'auto',
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  },
}));

export default AddDimensionDialog;
